import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import logo from '../../Assets/img/logo_2.png';
import './Preloader.css';

const loadingMessages = [
  'Connecting Career Counsellors',
  'Building Professional Networks',
  'Empowering Career Guidance',
  'Welcome to APCCI'
];

const Preloader = ({ onLoadingComplete }) => {
  const [progress, setProgress] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    let progressInterval;
    let messageInterval;
    let completeTimer;

    // Change loading message
    messageInterval = setInterval(() => {
      setMessageIndex((prev) =>
        prev < loadingMessages.length - 1 ? prev + 1 : prev
      );
    }, 700);

    // Animate progress
    progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }

        // Slightly variable progress for a more natural loading feel
        const increment = Math.floor(Math.random() * 4) + 1;

        return Math.min(prev + increment, 100);
      });
    }, 70);

    // Minimum 3-second loading screen
    completeTimer = setTimeout(() => {
      setProgress(100);

      setTimeout(() => {
        onLoadingComplete();
      }, 400);
    }, 3000);

    return () => {
      clearInterval(progressInterval);
      clearInterval(messageInterval);
      clearTimeout(completeTimer);
    };
  }, [onLoadingComplete]);

  return (
    <motion.div
      className="apcci-preloader"
      initial={{ opacity: 1 }}
      exit={{
        opacity: 0,
        scale: 1.03,
        transition: {
          duration: 0.7,
          ease: [0.4, 0, 0.2, 1]
        }
      }}
    >
      {/* Animated background elements */}
      <div className="preloader-background">
        <motion.div
          className="preloader-orb preloader-orb-one"
          animate={{
            x: [0, 40, 0],
            y: [0, -30, 0],
            scale: [1, 1.15, 1]
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />

        <motion.div
          className="preloader-orb preloader-orb-two"
          animate={{
            x: [0, -50, 0],
            y: [0, 40, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />

        <div className="preloader-grid" />
      </div>

      <div className="preloader-content">
        {/* Logo */}
        <motion.div
          className="preloader-logo-container"
          initial={{
            opacity: 0,
            scale: 0.7,
            y: 20
          }}
          animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }}
          transition={{
            duration: 0.8,
            ease: [0.16, 1, 0.3, 1]
          }}
        >
          <motion.div
            className="preloader-logo-glow"
            animate={{
              scale: [1, 1.15, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
          />

          <img
            src={logo}
            alt="APCCI Logo"
            className="preloader-logo"
          />
        </motion.div>

        {/* Organisation name */}
        <motion.div
          className="preloader-brand"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.7,
            delay: 0.3
          }}
        >
          <h1>APCCI</h1>

          <div className="preloader-divider">
            <motion.span
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{
                duration: 1,
                delay: 0.5
              }}
            />
          </div>

          <p>
            Association of Professional
            <br />
            Career Counsellors of India
          </p>
        </motion.div>

        {/* Loading information */}
        <motion.div
          className="preloader-loading-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: 0.6,
            delay: 0.7
          }}
        >
          <div className="preloader-message-container">
            <AnimatePresence mode="wait">
              <motion.p
                key={messageIndex}
                className="preloader-message"
                initial={{
                  opacity: 0,
                  y: 8
                }}
                animate={{
                  opacity: 1,
                  y: 0
                }}
                exit={{
                  opacity: 0,
                  y: -8
                }}
                transition={{
                  duration: 0.25
                }}
              >
                {loadingMessages[messageIndex]}
              </motion.p>
            </AnimatePresence>
          </div>

          {/* Progress bar */}
          <div className="preloader-progress-container">
            <motion.div
              className="preloader-progress-bar"
              animate={{
                width: `${progress}%`
              }}
              transition={{
                duration: 0.2,
                ease: 'easeOut'
              }}
            />

            <motion.div
              className="preloader-progress-light"
              animate={{
                left: `${progress}%`
              }}
              transition={{
                duration: 0.2,
                ease: 'easeOut'
              }}
            />
          </div>

          <div className="preloader-progress-info">
            <span>Preparing your experience</span>

            <motion.span
              key={progress}
              className="preloader-percentage"
            >
              {progress}%
            </motion.span>
          </div>
        </motion.div>

        {/* Bottom tagline */}
        <motion.p
          className="preloader-tagline"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: 1,
            delay: 1.2
          }}
        >
          Empowering Counsellors. Shaping Futures.
        </motion.p>
      </div>
    </motion.div>
  );
};

export default Preloader;